import * as React from 'react';
import { useCreate, useGetOne, useNotify, useRedirect, useUpdate } from 'react-admin';
import { useParams } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputAdornment,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import * as Yup from 'yup';
import { Formik, Form } from 'formik';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';
import { PageHeader } from '../../components/page-header/PageHeader';
import { FormBackButton } from '../../components/back-button/FormBackButton';
import { ScrollToFirstError } from '../../components/form/ScrollToFirstError';
import { Switch } from '../../components/switch/Switch';
import { labelAboveSx } from '../../core/theme/formStyles';
import { PAGE_GUTTER } from '../../core/theme/layout';
import { preventNumberInputScroll } from '../../utils/preventNumberInputScroll';
import {
  Coupon,
  COUPON_CODE_PATTERN,
  currencySymbol,
  DiscountType,
  formatMoney,
  MAX_PER_CUSTOMER_LIMIT,
  minRequiredOrderAmount,
} from './couponTypes';
import { useAccessibleLocations } from '../../hooks/useAccessibleLocations';

interface CouponFormValues {
  locationId: string;
  code: string;
  discountType: DiscountType;
  discountValue: string;
  minOrderAmount: string;
  usageLimit: string;
  isActive: boolean;
  combinableWithDiscounts: boolean;
  firstOrderOnly: boolean;
  hasPerCustomerLimit: boolean;
  perCustomerLimit: string;
}


const emptyToUndefined = (value: unknown, original: unknown) =>
  original === '' ? undefined : value;

// Jak w DiscountFormPage.
const toggleRowSx = {
  gap: 'var(--space-4)',
  p: 'var(--space-3) var(--space-4)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-sm)',
  cursor: 'pointer',
};

export default function CouponFormPage(): JSX.Element {
  const { t } = useTranslation();
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const notify = useNotify();
  const redirect = useRedirect();
  const [create] = useCreate();
  const [update] = useUpdate();
  const { locations, loading: locationsLoading } = useAccessibleLocations();

  const { data: coupon, isLoading: couponLoading } = useGetOne<Coupon>(
    'coupons',
    { id: Number(id) },
    { enabled: isEditMode },
  );
  const usedCount = coupon?.usedCount ?? 0;

  const validationSchema = Yup.object({
    locationId: Yup.string().required(t('coupons.validation.locationRequired')),
    code: Yup.string()
      .required(t('coupons.validation.codeRequired'))
      .matches(COUPON_CODE_PATTERN, t('coupons.validation.codeFormat')),
    discountType: Yup.string().oneOf(['PERCENT', 'AMOUNT']).required(),
    discountValue: Yup.number()
      .typeError(t('coupons.validation.discountRequired'))
      .required(t('coupons.validation.discountRequired'))
      .moreThan(0, t('coupons.validation.discountPositive'))
      .when('discountType', {
        is: 'PERCENT',
        then: (schema) => schema.lessThan(100, t('coupons.validation.percentMax')),
      }),
    // Puste pole = przy zapisie bierzemy minimum wyliczone z rabatu.
    minOrderAmount: Yup.number()
      .transform((value, original) => (original === '' ? undefined : value))
      .typeError(t('coupons.validation.minOrderAmountRequired'))
      .optional()
      .min(0, t('coupons.validation.minOrderAmountNonNegative'))
      .test('decimals', t('coupons.validation.minOrderAmountDecimals'), (value) =>
        value == null ? true : Math.abs(Math.round(value * 100) - value * 100) < 1e-6,
      )
      .test('min-for-discount', function (value) {
        const { discountType, discountValue, locationId } = this.parent as {
          discountType: DiscountType;
          discountValue: number;
          locationId: string;
        };
        const min = minRequiredOrderAmount(discountType, Number(discountValue));
        if (value == null || min == null || value >= min - 1e-9) return true;
        const currency = locations.find((l) => String(l.id) === locationId)?.currency;
        return this.createError({
          message: t('coupons.validation.minOrderAmountTooLow', { min: formatMoney(min, currency) }),
        });
      }),
    usageLimit: Yup.number()
      .typeError(t('coupons.validation.usageLimitRequired'))
      .required(t('coupons.validation.usageLimitRequired'))
      .integer(t('coupons.validation.usageLimitInteger'))
      .min(Math.max(1, usedCount), t('coupons.validation.usageLimitMin', { min: Math.max(1, usedCount) })),
    perCustomerLimit: Yup.number()
      .transform(emptyToUndefined)
      .when('hasPerCustomerLimit', {
        is: true,
        then: (schema) =>
          schema
            .typeError(t('coupons.validation.perCustomerLimitRange'))
            .required(t('coupons.validation.perCustomerLimitRequired'))
            .integer(t('coupons.validation.perCustomerLimitRange'))
            .min(1, t('coupons.validation.perCustomerLimitRange'))
            .max(MAX_PER_CUSTOMER_LIMIT, t('coupons.validation.perCustomerLimitRange'))
            .test('not-above-usage-limit', t('coupons.validation.perCustomerLimitAboveUsageLimit'), function (value) {
              const usageLimit = Number((this.parent as CouponFormValues).usageLimit);
              return value == null || !Number.isFinite(usageLimit) || value <= usageLimit;
            }),
        otherwise: (schema) => schema.optional(),
      }),
    // Przelaczniki sie wzajemnie blokuja, ale backend i tak odrzuci oba naraz (400).
    firstOrderOnly: Yup.boolean().test(
      'customer-rules-conflict',
      t('coupons.validation.customerRulesConflict'),
      function (value) {
        const { hasPerCustomerLimit } = this.parent as CouponFormValues;
        return !(value && hasPerCustomerLimit);
      },
    ),
  });

  const initialValues: CouponFormValues =
    isEditMode && coupon
      ? {
          locationId: String(coupon.locationId),
          code: coupon.code,
          discountType: coupon.discountType,
          discountValue: String(coupon.discountValue),
          minOrderAmount: coupon.minOrderAmount != null ? String(coupon.minOrderAmount) : '',
          usageLimit: String(coupon.usageLimit),
          isActive: coupon.isActive,
          combinableWithDiscounts: Boolean(coupon.combinableWithDiscounts),
          firstOrderOnly: coupon.firstOrderOnly ?? false,
          hasPerCustomerLimit: coupon.perCustomerLimit != null,
          perCustomerLimit: coupon.perCustomerLimit != null ? String(coupon.perCustomerLimit) : '',
        }
      : {
          locationId: locations.length === 1 ? String(locations[0].id) : '',
          code: '',
          discountType: 'PERCENT',
          discountValue: '',
          minOrderAmount: '',
          usageLimit: '',
          isActive: true,
          combinableWithDiscounts: false,
          firstOrderOnly: false,
          hasPerCustomerLimit: false,
          perCustomerLimit: '',
        };

  const handleSubmit = async (values: CouponFormValues) => {
    const location = locations.find((l) => String(l.id) === values.locationId);
    const data = {
      locationId: Number(values.locationId),
      locationName: location?.name ?? coupon?.locationName,
      code: values.code,
      discountType: values.discountType,
      discountValue: Number(values.discountValue),
      minOrderAmount:
        values.minOrderAmount === ''
          ? (minRequiredOrderAmount(values.discountType, Number(values.discountValue)) ?? 0)
          : Math.round(Number(values.minOrderAmount) * 100) / 100,
      usageLimit: Number(values.usageLimit),
      isActive: values.isActive,
      combinableWithDiscounts: values.combinableWithDiscounts,
      firstOrderOnly: values.firstOrderOnly,
      perCustomerLimit: values.hasPerCustomerLimit ? Number(values.perCustomerLimit) : null,
    };
    try {
      if (isEditMode && coupon) {
        await update('coupons', { id: coupon.id, data, previousData: coupon }, { returnPromise: true });
        notify(t('coupons.notifications.updated'), { type: 'success' });
      } else {
        await create('coupons', { data }, { returnPromise: true });
        notify(t('coupons.notifications.created'), { type: 'success' });
      }
      redirect('list', 'coupons');
    } catch (error: any) {
      // axiosHttpClient w dataProviderze odrzuca { status, message, body } — tresc bledu
      // z backendu ({ error: ... }) siedzi w `body`.
      notify(error?.body?.error ?? error?.message, { type: 'error' });
      Sentry.captureException(error);
    }
  };

  if (locationsLoading || (isEditMode && couponLoading)) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 'var(--space-16)' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({ values, handleChange, handleBlur, setFieldValue, errors, touched, isSubmitting }) => {
        const selectedLocation = locations.find((l) => String(l.id) === values.locationId);
        const unit =
          values.discountType === 'PERCENT' ? '%' : currencySymbol(selectedLocation?.currency);
        const requiredMin = minRequiredOrderAmount(values.discountType, Number(values.discountValue));

        return (
          <Form>
            <ScrollToFirstError />
            <PageHeader
              back={<FormBackButton />}
              title={isEditMode ? t('coupons.editCoupon') : t('coupons.addCoupon')}
              subtitle={isEditMode ? coupon?.code : undefined}
            />
            <Box sx={{ px: PAGE_GUTTER, py: { xs: 2, md: 4 }, maxWidth: 640, ...labelAboveSx }}>
              <Stack spacing={2.5}>
                <FormControl size="small" fullWidth error={touched.locationId && Boolean(errors.locationId)}>
                  <InputLabel shrink>{t('coupons.fields.location')}</InputLabel>
                  <Select
                    displayEmpty
                    name="locationId"
                    value={values.locationId}
                    onBlur={handleBlur}
                    onChange={handleChange}
                  >
                    <MenuItem value="" disabled>
                      {t('coupons.fields.locationPlaceholder')}
                    </MenuItem>
                    {locations.map((l) => (
                      <MenuItem key={l.id} value={String(l.id)}>
                        {l.name}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    {touched.locationId && errors.locationId ? errors.locationId : ' '}
                  </FormHelperText>
                </FormControl>

                <TextField
                  name="code"
                  size="small"
                  fullWidth
                  label={t('coupons.fields.code')}
                  placeholder="WELCOME10"
                  value={values.code}
                  onChange={(e) => setFieldValue('code', e.target.value.toUpperCase().replace(/\s/g, ''))}
                  onBlur={handleBlur}
                  error={touched.code && Boolean(errors.code)}
                  helperText={touched.code && errors.code ? errors.code : t('coupons.fields.codeHint')}
                  slotProps={{
                    inputLabel: { shrink: true },
                    htmlInput: { maxLength: 32, style: { fontFamily: 'monospace', letterSpacing: '0.04em' } },
                  }}
                />

                <FormControl>
                  <Typography sx={{ fontSize: 'var(--text-sm)', fontWeight: 500, color: 'text.secondary', mb: '3px' }}>
                    {t('coupons.fields.discountType')}
                  </Typography>
                  <RadioGroup
                    row
                    name="discountType"
                    value={values.discountType}
                    onChange={(e) => setFieldValue('discountType', e.target.value as DiscountType)}
                  >
                    <FormControlLabel
                      value="PERCENT"
                      control={<Radio size="small" />}
                      label={t('coupons.fields.discountPercent')}
                    />
                    <FormControlLabel
                      value="AMOUNT"
                      control={<Radio size="small" />}
                      label={t('coupons.fields.discountAmount')}
                    />
                  </RadioGroup>
                </FormControl>

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                    gap: 'var(--space-4)',
                  }}
                >
                  <TextField
                    name="discountValue"
                    type="number"
                    size="small"
                    fullWidth
                    label={t('coupons.fields.discountValue')}
                    placeholder={values.discountType === 'PERCENT' ? '10' : '15.00'}
                    value={values.discountValue}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    onWheel={preventNumberInputScroll}
                    error={touched.discountValue && Boolean(errors.discountValue)}
                    helperText={touched.discountValue && errors.discountValue ? errors.discountValue : ' '}
                    slotProps={{
                      inputLabel: { shrink: true },
                      htmlInput: {
                        min: 0,
                        max: values.discountType === 'PERCENT' ? 99.99 : undefined,
                        step: values.discountType === 'PERCENT' ? 1 : 0.01,
                        inputMode: 'decimal',
                      },
                      input: { endAdornment: <InputAdornment position="end">{unit}</InputAdornment> },
                    }}
                  />

                  <TextField
                    name="usageLimit"
                    type="number"
                    size="small"
                    fullWidth
                    label={t('coupons.fields.usageLimit')}
                    placeholder="100"
                    value={values.usageLimit}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    onWheel={preventNumberInputScroll}
                    error={touched.usageLimit && Boolean(errors.usageLimit)}
                    helperText={
                      touched.usageLimit && errors.usageLimit
                        ? errors.usageLimit
                        : isEditMode
                          ? t('coupons.fields.usedSoFar', { count: usedCount })
                          : t('coupons.fields.usageLimitHint')
                    }
                    slotProps={{
                      inputLabel: { shrink: true },
                      htmlInput: { min: 1, step: 1, inputMode: 'numeric' },
                    }}
                  />
                </Box>

                <TextField
                  name="minOrderAmount"
                  type="number"
                  size="small"
                  fullWidth
                  label={t('coupons.fields.minOrderAmount')}
                  placeholder={requiredMin != null ? requiredMin.toFixed(2) : undefined}
                  value={values.minOrderAmount}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  onWheel={preventNumberInputScroll}
                  error={touched.minOrderAmount && Boolean(errors.minOrderAmount)}
                  helperText={
                    touched.minOrderAmount && errors.minOrderAmount
                      ? errors.minOrderAmount
                      : requiredMin != null
                        ? t('coupons.fields.minOrderAmountHint', {
                            min: formatMoney(requiredMin, selectedLocation?.currency),
                          })
                        : t('coupons.fields.minOrderAmountHintEmpty')
                  }
                  slotProps={{
                    inputLabel: { shrink: true },
                    htmlInput: { min: 0, step: 0.01, inputMode: 'decimal' },
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">{currencySymbol(selectedLocation?.currency)}</InputAdornment>
                      ),
                    },
                  }}
                />

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                  <Typography sx={{ fontSize: 'var(--text-sm)', fontWeight: 500, color: 'text.secondary' }}>
                    {t('coupons.fields.customerRules')}
                  </Typography>
                  {/* Jedno ograniczenie naraz: wlaczony przelacznik blokuje drugi (jak backend). */}
                  <Stack
                    component="label"
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{
                      ...toggleRowSx,
                      ...(values.hasPerCustomerLimit && { cursor: 'default' }),
                    }}
                  >
                    <Box>
                      <Typography
                        sx={{
                          fontSize: 'var(--text-sm)',
                          color: values.hasPerCustomerLimit ? 'var(--color-text-disabled)' : 'text.primary',
                        }}
                      >
                        {t('coupons.fields.firstOrderOnly')}
                      </Typography>
                      <Typography sx={{ fontSize: 'var(--text-xs)', color: 'text.secondary' }}>
                        {t('coupons.fields.firstOrderOnlyHint')}
                      </Typography>
                    </Box>
                    <Switch
                      checked={values.firstOrderOnly}
                      disabled={values.hasPerCustomerLimit}
                      onChange={(e) => setFieldValue('firstOrderOnly', e.target.checked)}
                      inputProps={{ 'aria-label': t('coupons.fields.firstOrderOnly') }}
                    />
                  </Stack>
                  <Stack
                    component="label"
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{
                      ...toggleRowSx,
                      ...(values.firstOrderOnly && { cursor: 'default' }),
                    }}
                  >
                    <Box>
                      <Typography
                        sx={{
                          fontSize: 'var(--text-sm)',
                          color: values.firstOrderOnly ? 'var(--color-text-disabled)' : 'text.primary',
                        }}
                      >
                        {t('coupons.fields.hasPerCustomerLimit')}
                      </Typography>
                      <Typography sx={{ fontSize: 'var(--text-xs)', color: 'text.secondary' }}>
                        {t('coupons.fields.hasPerCustomerLimitHint')}
                      </Typography>
                    </Box>
                    <Switch
                      checked={values.hasPerCustomerLimit}
                      disabled={values.firstOrderOnly}
                      onChange={(e) => {
                        setFieldValue('hasPerCustomerLimit', e.target.checked);
                        // Najczestszy przypadek to kod jednorazowy - podpowiadamy 1.
                        if (e.target.checked && values.perCustomerLimit === '') {
                          setFieldValue('perCustomerLimit', '1');
                        }
                      }}
                      inputProps={{ 'aria-label': t('coupons.fields.hasPerCustomerLimit') }}
                    />
                  </Stack>
                  {values.hasPerCustomerLimit && (
                    <TextField
                      name="perCustomerLimit"
                      type="number"
                      size="small"
                      fullWidth
                      label={t('coupons.fields.perCustomerLimit')}
                      placeholder="1"
                      value={values.perCustomerLimit}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      onWheel={preventNumberInputScroll}
                      error={touched.perCustomerLimit && Boolean(errors.perCustomerLimit)}
                      helperText={
                        touched.perCustomerLimit && errors.perCustomerLimit ? errors.perCustomerLimit : ' '
                      }
                      slotProps={{
                        inputLabel: { shrink: true },
                        htmlInput: { min: 1, max: MAX_PER_CUSTOMER_LIMIT, step: 1, inputMode: 'numeric' },
                      }}
                    />
                  )}
                  {errors.firstOrderOnly && <FormHelperText error>{errors.firstOrderOnly}</FormHelperText>}
                </Box>

                <Stack
                  component="label"
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={toggleRowSx}
                >
                  <Box>
                    <Typography sx={{ fontSize: 'var(--text-sm)', color: 'text.primary' }}>
                      {t('coupons.fields.combinable')}
                    </Typography>
                    <Typography sx={{ fontSize: 'var(--text-xs)', color: 'text.secondary' }}>
                      {t('coupons.fields.combinableHint')}
                    </Typography>
                  </Box>
                  <Switch
                    checked={values.combinableWithDiscounts}
                    onChange={(e) => setFieldValue('combinableWithDiscounts', e.target.checked)}
                    inputProps={{ 'aria-label': t('coupons.fields.combinable') }}
                  />
                </Stack>

                <Stack
                  component="label"
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  spacing={2}
                  sx={{
                    p: 'var(--space-3) var(--space-4)',
                    border: '1px solid var(--color-border)',
                    borderRadius: 'var(--radius-sm)',
                    cursor: 'pointer',
                  }}
                >
                  <Box>
                    <Typography sx={{ fontSize: 'var(--text-sm)', color: 'text.primary' }}>
                      {t('coupons.fields.isActive')}
                    </Typography>
                    <Typography sx={{ fontSize: 'var(--text-xs)', color: 'text.secondary' }}>
                      {t('coupons.fields.isActiveHint')}
                    </Typography>
                  </Box>
                  <Switch
                    checked={values.isActive}
                    onChange={(e) => setFieldValue('isActive', e.target.checked)}
                    inputProps={{ 'aria-label': t('coupons.fields.isActive') }}
                  />
                </Stack>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-3)', pt: 1 }}>
                  <Button
                    variant="outlined"
                    type="button"
                    onClick={() => redirect('list', 'coupons')}
                    sx={{
                      borderRadius: 'var(--radius-sm)',
                      borderColor: 'var(--color-error)',
                      color: 'var(--color-error)',
                      '&:hover': {
                        borderColor: 'var(--color-error-hover)',
                        backgroundColor: 'var(--color-error-soft)',
                        color: 'var(--color-error-hover)',
                      },
                    }}
                  >
                    {t('common.cancel')}
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={isSubmitting}
                    sx={{
                      borderRadius: 'var(--radius-sm)',
                      backgroundColor: 'var(--color-primary)',
                      color: 'var(--color-text-inverse)',
                      boxShadow: 'none',
                      '&:hover': {
                        backgroundColor: 'var(--color-primary-hover)',
                        color: 'var(--color-text-inverse)',
                      },
                    }}
                  >
                    {isSubmitting ? (
                      <CircularProgress size={25} sx={{ color: 'white' }} />
                    ) : isEditMode ? (
                      t('common.saveChanges')
                    ) : (
                      t('common.add')
                    )}
                  </Button>
                </Box>
              </Stack>
            </Box>
          </Form>
        );
      }}
    </Formik>
  );
}
